/**
 * WebSocket Service for Real-Time PDF Collaboration
 * 
 * Handles WebSocket connections to the backend for live layer updates,
 * cursor sharing, and presence tracking.
 */

export interface Layer {
  id: string;
  layer_document: string;
  page_number: number;
  layer_type: 'shape' | 'path' | 'text' | 'annotation' | 'measurement' | 'image' | 'hyperlink';
  data: any;
  z_index: number;
  visible: boolean;
  markup_scope: 'personal' | 'shared';
  punch_list_item: string | null;
  observation: string | null;
  submittal: string | null;
  rfi: string | null;
  created_by?: User;
  created_at?: string;
  updated_at?: string;
}

export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
}

export interface CursorPosition {
  x: number;
  y: number;
}

export interface WebSocketMessage {
  type: string;
  [key: string]: any;
}

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

export interface WebSocketServiceCallbacks {
  onLayerCreated?: (layer: Layer, user: User) => void;
  onLayerUpdated?: (layer: Layer, user: User) => void;
  onLayerDeleted?: (layerId: string, user: User) => void;
  onCursorMoved?: (user: User, position: CursorPosition, pageNumber?: number) => void;
  onLayerSelected?: (user: User, layerId?: string) => void;
  onUserJoined?: (user: User) => void;
  onUserLeft?: (user: User) => void;
  onPresenceSync?: (users: User[]) => void;
  onStateSync?: (version: number, layers: Layer[]) => void;
  onConnectionStatusChange?: (status: ConnectionStatus) => void;
  onError?: (error: string) => void;
}

interface WebSocketUrlOptions {
  pageUrl: string;
  apiBaseUrl?: string;
  wsHost?: string;
  wsPort?: string;
}

export const buildWebSocketUrl = (
  layerDocId: string,
  { pageUrl, apiBaseUrl, wsHost, wsPort }: WebSocketUrlOptions
): string => {
  const page = new URL(pageUrl);
  const api = new URL(apiBaseUrl || '/api/v1', page.origin);
  const protocol = api.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = wsHost || api.hostname;
  const hasExplicitApiOrigin = Boolean(
    apiBaseUrl && /^[a-z][a-z\d+.-]*:\/\//i.test(apiBaseUrl)
  );
  const inferredPort = hasExplicitApiOrigin
    ? api.port
    : (page.port === '5173' ? '8080' : page.port);
  const port = wsPort || inferredPort;
  const portSuffix = port ? `:${port}` : '';
  return `${protocol}//${host}${portSuffix}/ws/drawings/layer-documents/${layerDocId}/`;
};

export class WebSocketService {
  private ws: WebSocket | null = null;
  private layerDocId: string;
  private clientId: string;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second
  private pingInterval: NodeJS.Timeout | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private shouldReconnect = false;
  private callbacks: WebSocketServiceCallbacks;
  private connectionStatus: ConnectionStatus = 'disconnected';
  
  constructor(layerDocId: string, callbacks: WebSocketServiceCallbacks = {}) {
    this.layerDocId = layerDocId;
    this.clientId = this.generateClientId();
    this.callbacks = callbacks;
  }
  
  /**
   * Connect to the WebSocket server
   */
  connect(): void {
    this.shouldReconnect = true;
    this.clearReconnectTimeout();

    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)
    ) {
      console.warn('WebSocket already connected');
      return;
    }
    
    this.updateConnectionStatus('connecting');
    
    // Use the API origin for WebSockets so the browser sends the same
    // host-scoped session cookie when the frontend is served from `www` but
    // the authenticated API lives on the apex domain.
    const wsUrl = buildWebSocketUrl(this.layerDocId, {
      pageUrl: window.location.href,
      apiBaseUrl: import.meta.env.VITE_API_URL,
      wsHost: import.meta.env.VITE_WS_HOST,
      wsPort: import.meta.env.VITE_WS_PORT,
    });
    
    console.log(`Connecting to WebSocket: ${wsUrl}`);
    
    try {
      this.ws = new WebSocket(wsUrl);
      this.setupEventHandlers();
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      this.updateConnectionStatus('error');
      this.scheduleReconnect();
    }
  }
  
  /**
   * Disconnect from the WebSocket server
   */
  disconnect(): void {
    this.shouldReconnect = false;
    this.clearReconnectTimeout();
    this.stopPingInterval();
    
    if (this.ws) {
      this.ws.close(1000, 'Client disconnecting');
      this.ws = null;
    }
    
    this.updateConnectionStatus('disconnected');
    this.reconnectAttempts = 0;
  }
  
  /**
   * Send a layer creation request
   */
  sendLayerCreate(layerData: Omit<Layer, 'id' | 'created_at' | 'updated_at' | 'created_by'>): void {
    this.send({
      type: 'layer.create',
      payload: layerData,
      client_id: this.clientId,
    });
  }
  
  /**
   * Send a layer update request
   */
  sendLayerUpdate(layerId: string, updates: Partial<Layer>): void {
    this.send({
      type: 'layer.update',
      layer_id: layerId,
      payload: updates,
      client_id: this.clientId,
    });
  }
  
  /**
   * Send a layer deletion request
   */
  sendLayerDelete(layerId: string): void {
    this.send({
      type: 'layer.delete',
      layer_id: layerId,
      client_id: this.clientId,
    });
  }
  
  /**
   * Send cursor position update
   */
  sendCursorMove(position: CursorPosition, pageNumber?: number): void {
    this.send({
      type: 'cursor.move',
      position,
      page_number: pageNumber,
    });
  }
  
  /**
   * Send layer selection update
   */
  sendLayerSelect(layerId?: string): void {
    this.send({
      type: 'layer.select',
      layer_id: layerId,
    });
  }
  
  /**
   * Get current connection status
   */
  getConnectionStatus(): ConnectionStatus {
    return this.connectionStatus;
  }
  
  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }
  
  // ============================================
  // Private Methods
  // ============================================
  
  private setupEventHandlers(): void {
    if (!this.ws) return;
    
    this.ws.onopen = this.handleOpen.bind(this);
    this.ws.onmessage = this.handleMessage.bind(this);
    this.ws.onclose = this.handleClose.bind(this);
    this.ws.onerror = this.handleError.bind(this);
  }
  
  private handleOpen(): void {
    console.log('WebSocket connected');
    this.updateConnectionStatus('connected');
    this.reconnectAttempts = 0;
    this.reconnectDelay = 1000;
    this.startPingInterval();
  }
  
  private handleMessage(event: MessageEvent): void {
    try {
      const data: WebSocketMessage = JSON.parse(event.data);
      
      // Ignore messages from self (except state sync)
      if (data.client_id === this.clientId && data.type !== 'state.sync') {
        return;
      }
      
      // Route to appropriate callback
      switch (data.type) {
        case 'layer.created':
          this.callbacks.onLayerCreated?.(data.layer, data.user);
          break;
          
        case 'layer.updated':
          this.callbacks.onLayerUpdated?.(data.layer, data.user);
          break;
          
        case 'layer.deleted':
          this.callbacks.onLayerDeleted?.(data.layer_id, data.user);
          break;
          
        case 'cursor.moved':
          this.callbacks.onCursorMoved?.(data.user, data.position, data.page_number);
          break;
          
        case 'layer.selected':
          this.callbacks.onLayerSelected?.(data.user, data.layer_id);
          break;
          
        case 'user.joined':
          this.callbacks.onUserJoined?.(data.user);
          break;
          
        case 'user.left':
          this.callbacks.onUserLeft?.(data.user);
          break;

        case 'presence.sync':
          this.callbacks.onPresenceSync?.(Array.isArray(data.users) ? data.users : []);
          break;
          
        case 'state.sync':
          this.callbacks.onStateSync?.(data.version, data.layers);
          break;
          
        case 'error':
          console.error('Server error:', data.message);
          this.callbacks.onError?.(data.message);
          break;
          
        case 'pong':
          // Keepalive response
          break;
          
        default:
          console.warn('Unknown message type:', data.type);
      }
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  }
  
  private handleClose(event: CloseEvent): void {
    console.log(`WebSocket closed: ${event.code} - ${event.reason}`);
    this.stopPingInterval();
    this.updateConnectionStatus('disconnected');
    
    // Attempt to reconnect unless it was a clean close
    if (
      this.shouldReconnect &&
      event.code !== 1000 &&
      this.reconnectAttempts < this.maxReconnectAttempts
    ) {
      this.scheduleReconnect();
    }
  }
  
  private handleError(event: Event): void {
    console.error('WebSocket error:', event);
    this.updateConnectionStatus('error');
  }
  
  private scheduleReconnect(): void {
    if (!this.shouldReconnect || this.reconnectTimeout) return;

    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 30000);
    
    console.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
    
    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      if (!this.shouldReconnect) return;

      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.connect();
      } else {
        console.error('Max reconnection attempts reached');
        this.callbacks.onError?.('Connection lost. Please refresh the page.');
      }
    }, delay);
  }

  private clearReconnectTimeout(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }
  
  private send(data: any): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      console.warn('WebSocket not connected. Message not sent:', data);
    }
  }
  
  private startPingInterval(): void {
    // Send ping every 30 seconds to keep connection alive
    this.pingInterval = setInterval(() => {
      this.send({ type: 'ping' });
    }, 30000);
  }
  
  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }
  
  private updateConnectionStatus(status: ConnectionStatus): void {
    if (this.connectionStatus !== status) {
      this.connectionStatus = status;
      this.callbacks.onConnectionStatusChange?.(status);
    }
  }
  
  private generateClientId(): string {
    return `client_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }
}

export default WebSocketService;
